import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps } from "react"

import { cn } from "~/lib/utils"

const badgeVariants = cva(
	"inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden truncate",
	{
		variants: {
			variant: {
				default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
				deep: "bg-primary/70 text-primary-foreground [a&]:hover:bg-primary/90",
				secondary:
					"bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
				destructive:
					"bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/70",
				count: "bg-background text-text-small tabular-nums text-[10px]",
				success: "border border-green-500/40 bg-green-500/10 text-green-400",
				warning: "border border-yellow-500/40 bg-yellow-500/10 text-yellow-400",
				outline:
					"text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground"
			}
		},
		compoundVariants: [
			{
				variant: ["default", "deep", "secondary", "destructive", "outline"],
				className: "control-raised"
			}
		],
		defaultVariants: {
			variant: "default"
		}
	}
)

function Badge({
	className,
	variant,
	asChild = false,
	...props
}: ComponentProps<"span"> &
	VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : "span"

	return (
		<Comp
			data-slot="badge"
			className={cn(badgeVariants({ variant }), className)}
			{...props}
		/>
	)
}

export { Badge, badgeVariants }
